/**
 * These are used for toast notifications flying in from above:
 * https://zappy.zapier.com/778482F6-F63A-4B8F-A373-4E90D40BB8A2.png
 */
import _ from 'lodash';

import * as statuses from 'app/entity/utils/statuses';
import buildEntityActions from 'app/entity/utils/buildEntityActions';
import buildEntitySelectors from 'app/entity/utils/buildEntitySelectors';

// Note that this is a client-side only entity.
export const type = 'toastNotification';

export const selectors = buildEntitySelectors(type, sel => {
  sel.entityCid = _.partial(sel.dataItem, 'id');
  sel.type = _.partial(sel.stateItem, 'type');
  sel.message = _.partial(sel.stateItem, 'message');
  sel.dismissAfter = _.partial(sel.stateItem, 'dismissAfter');
  sel.isForEntity = (otherEntity, entity) =>
    sel.cid(otherEntity) === sel.id(entity);
  sel.all.forEntity = _.partial(sel.all.findBy, sel.isForEntity);
  sel.all.forEntityBy = (selectorFn, state) => {
    const entity = selectorFn(state);
    return sel.all.forEntity(entity, state);
  };
  sel.all.first = _.flow(
    sel.all.entities,
    _.first
  );
});

// Notifications are client-side only, so just stub out `api`
const api = {};

export const actions = buildEntityActions(type, selectors, api);

const normalizeNotificationType = statusType => {
  if (statusType === statuses.FAILURE) {
    statusType = 'error';
  }
  return statusType;
};

actions.create = (notificationType, message, id, dismissAfter) => {
  return message
    ? actions.createCollectionUiEntity(
        {
          id: id || _.uniqueId('notification-'),
        },
        {
          dismissAfter: dismissAfter || 5000,
          message,
          type: normalizeNotificationType(notificationType),
        }
      )
    : { type: 'NOOP_ACTION' };
};

// Create a one-off notification that isn't tied to an entity,
// collection, etc.
actions.createSuccess = _.partial(actions.create, statuses.SUCCESS);
actions.createFailure = _.partial(actions.create, statuses.FAILURE);
actions.createInfo = _.partial(actions.create, 'info');

// Create a notification for a specific `entity`, passing its
// `Entity` "class" along so its `selectors` can be used.
// The `Notification` `type` and `message` will be derived from
// the `status` and `statusMessage` of the entity, respectively.
actions.createNotificationForEntity = (Entity, entity) =>
  actions.create(
    Entity.selectors.status(entity),
    Entity.selectors.statusMessage(entity),
    Entity.selectors.cid(entity)
  );

// Create a notification for a specific entity collection passing
// its `Entity` "class" along so its `selectors` can be used.
// The `Notification` `type` and `message` will be derived from
// the `status` and `statusMessage` of the collection, respectively.
actions.createNotificationForCollection = (entityId, Entity, state) =>
  actions.create(
    Entity.selectors.all.status(state),
    Entity.selectors.all.statusMessage(state),
    entityId
  );

actions.removeNotificationForEntity = (Entity, entity) =>
  actions.deleteCollectionEntities(Entity.selectors.cid(entity));
